import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Actions, ofType, createEffect, concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { switchMap, map, filter, tap, catchError, takeUntil, mergeMap } from 'rxjs/operators';

import { GoToSection } from '../../../navigation/store/navigation.actions';
import * as AnalyticsActions from '../analytics.actions';
import * as AppEventsAnalyticsActions from '../app-events-analytics.actions';

import { Cart } from '@api/cart/cart.models';
import { GeolocationDetail } from '@api/common/models/geolocation.model';
import { ShippingMethod } from '@api/shipping/models/shipping.model';
import { DroppointListsDetail } from '@api/store-locator/droppoint-lists/models/droppoint-lists.models';
import { PhysicalStoreDetail } from '@api/store-locator/physical-store/models/physical-store.models';
import { BrowserOnly } from '@core/data-providers/platform/platform.decorators';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { EcommerceContext } from '@core/domain/models/context.models';
import { CurrencyInfo } from '@core/domain/models/store.models';
import { getContext, getCurrencyInfo } from '@core/infrastructure/state/core.selectors';
import { CoreStorageService } from '@core/services/storage/storage.service';
import { PublicUser } from '@features/user';
import { SetSelectedPickupPoint } from '@pages/checkout/shipping/store/pickup/pickup.actions';
import { getPickupPointsViewMode, getSelectedPickupPoint } from '@pages/checkout/shipping/store/pickup/pickup.selectors';
import { SendCheckoutShippingSuccess } from '@pages/checkout/shipping/store/shipping/shipping.actions';
import { getSelectedShippingMethod } from '@pages/checkout/shipping/store/shipping/shipping.selectors';
import { SaveShippingAddressSuccess } from '@shared/address/store/address/address.actions';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { ConfigurationService } from '@shared/analytics/COMMON/services/configuration.service';
import { GA4_PURCHASE_EVENT_PENDING_STORE_KEY } from '@shared/analytics/GA4/constants/ga4-analytics.constants';
import { GA4EventType } from '@shared/analytics/GA4/models/general.model';
import { GA4AnalyticsPublicActions } from '@shared/analytics/GA4/public.api';
import { sendObservabilityLog } from '@shared/analytics/ITXObservabilityCore/helper';
import { EventData } from '@shared/analytics/models/general/general.models';
import {
    imCheckoutCheckoutBillingDataOk,
    imCheckoutCheckoutEvent, imCheckoutCheckoutNextStepEvent,
    imCheckoutCreateShopCart,
    imCheckoutCreateSummary, imCheckoutOpenPrivacyPolicy, imCheckoutOpenPurchaseConditions,
    imCheckoutSelectWalletCard, imCheckoutSendCheckoutBackEvent, imCheckoutSendCheckoutConfirmPlaceEvent,
    imCheckoutSendCheckoutSelectPlaceEvent,
} from '@shared/analytics/services/checkout/im-checkout.helper';
import { ImShippingService } from '@shared/analytics/services/shipping/im-shipping.service';
import { AnalyticsEvent, ShippingEvent } from '@shared/analytics/store/analytics.actions';
import { getFastSintEnabled } from '@shared/fast-sint/store/fast-sint.selectors';
import * as NavigationButtonActions from '@shared/navigation-button/store/navigation-button.actions';
import { getActivePath } from '@shared/navigation-button/store/navigation-button.selectors';
import { getPaymentDetails, getPaymentDetailsValid, getSelectedPaymentMethod } from '@shared/payment/store';
import { Checkout, SetSyncCheckoutLaunched } from '@shared/purchase/store/purchase.actions';
import { getNeedBilling, getSyncCheckoutLaunched } from '@shared/purchase/store/purchase.selectors';
import { NostromoService } from '@shared/services/nostromo.service';
import { getShopCart } from '@shared/shopcart/store/shopcart.selectors';
import { OpenDocument } from '@shared/terms-conditions/store/terms-conditions.actions';
import { getUserSelector } from '@shared/user/store';

export const ZENIT_PURCHASE_EVENT_PENDING = 'ZenitPurchaseEventPending';

@Injectable()
export class AnalyticsCheckoutEffects {
    constructor(
        private readonly store: Store,
        private readonly actions$: Actions,
        private readonly imShippingService: ImShippingService,
        private readonly nostromoService: NostromoService,
        private readonly configurationService: ConfigurationService,
        @Inject(WINDOW) private readonly window: CommonWindow,
        private readonly storageService: CoreStorageService,
    ) { }

    private readonly getContext$: Observable<EcommerceContext> = this.store.select(getContext);

    private readonly userFromApi$: Observable<PublicUser> = this.store.select(
        getUserSelector
    ).pipe(filter((user) => !!user));

    private readonly shopCart$: Observable<Cart> = this.store.select(getShopCart);

    private readonly selectedPickupPoint$: Observable<GeolocationDetail> = this.store.select(getSelectedPickupPoint);

    private readonly paymentData$: Observable<any> = this.store.select(getSelectedPaymentMethod);

    private readonly isFastSint$: Observable<boolean> = this.store.select(getFastSintEnabled);

    private readonly getCurrencyInfo$: Observable<CurrencyInfo> = this.store.select(getCurrencyInfo);

    /* ################################## Eventos GA4 ################################## */
    public EventShippingInfoGa4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SendCheckoutShippingSuccess),
            switchMap(() => this.actions$.pipe(ofType(GoToSection))),
            filter((action) => ['payment', 'summary'].includes(action.url)),
            concatLatestFrom(() => this.store.select(getActivePath)),
            filter(([, activePath]) => activePath === 'shipping'),
            map(() =>
                GA4AnalyticsPublicActions.Ga4Event({
                    name: GA4EventType.add_shipping_info,
                    eventFrom: EventFrom.checkout,
                })),
        );
    });

    public EventPaymentInfoGa4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.EventPaymentOkAnalytics),
            map(() =>
                GA4AnalyticsPublicActions.Ga4Event({
                    name: GA4EventType.add_payment_info,
                    eventFrom: EventFrom.checkout,
                })),
        );
    });

    public MarkPurchaseEventPendig$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(Checkout),
            tap(({ orderId }) => { this.markPurchaseEventPendig(this.storageService, orderId); })
        );
    }, { dispatch: false });

    @BrowserOnly<AnalyticsCheckoutEffects, undefined>(undefined)
    private markPurchaseEventPendig(coreStorageService: CoreStorageService, orderId: string): void {
        coreStorageService.setValue(GA4_PURCHASE_EVENT_PENDING_STORE_KEY, orderId, 'SESSION', 'ANALYTICS');
        coreStorageService.setValue(ZENIT_PURCHASE_EVENT_PENDING, orderId, 'SESSION', 'ANALYTICS');
    }

    /* ################################## Eventos UA ################################## */

    public createShopCart$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.CheckoutEventCreateShopCartAnalytics),
            // eslint-disable-next-line rxjs/no-unsafe-switchmap
            switchMap((action) => this.configurationService.getIsGTMLoadedWithActualInfo$<typeof action>(action)),
            filter((action) => !!action),
            concatLatestFrom(() => this.isFastSint$),
            // eslint-disable-next-line rxjs/no-unsafe-switchmap
            switchMap(([action, isFastSint]) => {
                // TODO ECOMDUTI-21788 crear servicio estático para los logs y así evitar poner eslint-disable
                // eslint-disable-next-line no-console
                console.log('analytics-checkpout-switchmap', action);
                const checkoutEventCreateShopCart$: Subject<void> = new Subject();
                return combineLatest([
                    this.getContext$,
                    this.userFromApi$,
                    this.shopCart$,
                    of(action),
                    of(checkoutEventCreateShopCart$),
                    of(isFastSint),
                ]).pipe(
                    takeUntil(checkoutEventCreateShopCart$)
                );
            }),
            filter(([context, user, shopCart]) => {
                return (
                    context
                    && Object.keys(context).length >= 1
                    && user
                    && Object.keys(user).length >= 1
                    && shopCart
                    && Object.keys(shopCart).length >= 1
                );
            }),
            map(([context, user, shopCart, action, passTrough, isfastSint]) => {
                const { title, step, url } = action;
                passTrough.next();
                passTrough.complete();
                try {
                    imCheckoutCreateShopCart(
                        shopCart,
                        title,
                        step,
                        user,
                        context,
                        this.window,
                        this.storageService,
                        url,
                        isfastSint
                    );
                    const dataObject = {};
                    return AnalyticsActions.LoadEventSuccess({
                        payload: {
                            type: 'createShopCart',
                            dataObject,
                            eventFrom: EventFrom.checkout,
                        },
                    });
                } catch (error) {
                    sendObservabilityLog(error, 'createShopCart$');
                    console.error(error);
                    return AnalyticsActions.LoadEventError();
                }
            }),
        );
    });

    public saveAddressSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SaveShippingAddressSuccess),
            map(({ shippingAddress }) => this.imShippingService.addAddress(shippingAddress)),
            map(({ eventData, customData }) =>
                AnalyticsActions.SendCustomAnalytics({
                    eventData,
                    customData,
                })),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public createSummary$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.CheckoutEventCreateSummaryAnalytics),
            // eslint-disable-next-line rxjs/no-unsafe-switchmap
            switchMap((action) => this.configurationService.getIsGTMLoadedWithActualInfo$<typeof action>(action)),
            filter((action) => !!action),
            // eslint-disable-next-line rxjs/no-unsafe-switchmap
            switchMap((action) => {
                const checkoutEventCreateSummary$: Subject<void> = new Subject();
                return combineLatest([
                    this.getContext$,
                    this.userFromApi$,
                    this.shopCart$,
                    this.selectedPickupPoint$,
                    of(checkoutEventCreateSummary$),
                    of(action.url),
                    this.getCurrencyInfo$,
                ]).pipe(takeUntil(checkoutEventCreateSummary$));
            }),
            filter(([context, user, shopCart]) => {
                return (
                    context
                    && Object.keys(context).length >= 1
                    && user
                    && Object.keys(user).length >= 1
                    && shopCart
                    && Object.keys(shopCart).length >= 1
                );
            }),
            // eslint-disable-next-line rxjs/no-unsafe-switchmap
            switchMap((action) => combineLatest([this.isFastSint$, of(action)])),
            tap(([isFastSint, [context, user, shopCart, store, passTrough, url, currencyInfo]]) => {
                // TODO ECOMDUTI-21014 migrar a modelo general de analitica
                passTrough.next();
                passTrough.complete();
                imCheckoutCreateSummary(
                    shopCart,
                    store,
                    user,
                    context,
                    this.window,
                    this.storageService,
                    currencyInfo,
                    url,
                    isFastSint
                );
            }),
            map(() => {
                const dataObject = {};
                return AnalyticsActions.LoadEventSuccess({
                    payload: {
                        type: 'createSummary',
                        dataObject,
                        eventFrom: EventFrom.checkout,
                    },
                });
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public eventPaymentOk$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.EventPaymentOkAnalytics),
            concatLatestFrom(() => this.store.select(getSelectedPaymentMethod)),
            map(([_, payment]) => {
                const label: string = payment.type.toLowerCase();
                return AnalyticsActions.AnalyticsEvent({
                    eventData: {
                        cf: 'checkout',
                        category: 'pago',
                        action: 'seleccionar_pago',
                        label,
                        eventFrom: EventFrom.checkout,
                    },
                });
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public selectWalletCard$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.CheckoutEventSelectWalletCardAnalytics),
            map(({ card }) => imCheckoutSelectWalletCard(card)),
            map(({ eventData, customData }) =>
                AnalyticsActions.SendCustomAnalytics({
                    eventData,
                    customData,
                })),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public ShippingCreateEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AnalyticsActions.ShippingEvent),
            map((actionData) => {
                const { eventData: { cf, action, label }, customData } = actionData;
                return this.imShippingService.createEvent(cf, action, label, customData);
            }),
            map(({ eventData, customData }) =>
                AnalyticsActions.SendCustomAnalytics({
                    eventData: {
                        ...eventData,
                        eventFrom: EventFrom.checkout,
                    },
                    customData,
                })),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public SelectShippingMethod$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.SelectShippingMethodEventAnalytics),
            map((action) => {
                return this.imShippingService.selectShipping(action.method, 'seleccionar_envio_ok');
            }),
            map((event) => {
                return AnalyticsActions.AnalyticsEvent({
                    eventData: {
                        ...event,
                        eventFrom: EventFrom.checkout,
                    },
                });
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public SetNextStepEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationButtonActions.SetNextStatus),
            concatLatestFrom(() => this.store.select(getActivePath)),
            map(([_, activepath]) => imCheckoutCheckoutNextStepEvent(activepath)),
            map((eventData: EventData) =>
                AnalyticsActions.AnalyticsEvent({
                    eventData: {
                        ...eventData,
                        eventFrom: EventFrom.checkout,
                    },
                })),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public SelectFavoriteStoreEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.SelectFavoriteStoreEventAnalytics),
            map(({ storeId }) => this.imShippingService.selectedFavorite(storeId)),
            map(({ eventData, customData }) =>
                AnalyticsActions.SendCustomAnalytics({
                    eventData: {
                        ...eventData,
                        eventFrom: EventFrom.checkout,
                    },
                    customData,
                })),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public ShippingServerErrorEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.ShippingServerErrorEventAnalytics),
            map((action) => {
                const { form, error } = action;
                return this.imShippingService.createServer(form, error);
            }),
            map((eventData) =>
                AnalyticsActions.AnalyticsEvent({
                    eventData: {
                        ...eventData,
                        eventFrom: EventFrom.checkout,
                    },
                })),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public CheckoutEventLoadAsyncCheckout$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.CheckoutEventLoadAsyncCheckoutAnalytics),
            switchMap(() => {
                const checkoutEventLoadAsyncCheckout$: Subject<void> = new Subject();
                return combineLatest([
                    this.shopCart$,
                    this.paymentData$,
                    of(checkoutEventLoadAsyncCheckout$),
                    this.store.select(getSelectedPaymentMethod),
                    this.store.select(getSyncCheckoutLaunched),
                    this.store.select(getNeedBilling),
                ]).pipe(takeUntil(checkoutEventLoadAsyncCheckout$));
            }),
            filter(([shopCart, paymentData]) => {
                return paymentData && Object.keys(paymentData).length >= 1 && shopCart && Object.keys(shopCart).length >= 1;
            }),
            tap(([_shopCart, _paymentData, _passTrough, selectedPayment, syncCheckoutLaunched]) => {
                if (!syncCheckoutLaunched) {
                    this.nostromoService.payment(selectedPayment.code);
                    this.store.dispatch(SetSyncCheckoutLaunched());
                }
            }),
            map(([shopCart, paymentData, passTrough, _, __, isNeedBilling]) => {
                passTrough.next();
                passTrough.complete();
                return imCheckoutCheckoutEvent(paymentData, shopCart, isNeedBilling);
            }),
            mergeMap((analyticsEvents) => {
                const actions = [
                    AnalyticsActions.SendCustomAnalytics({
                        eventData: {
                            ...analyticsEvents[0].eventData,
                            eventFrom: EventFrom.checkout,
                        },
                        customData: {
                            ...analyticsEvents[0].customData,
                        },
                        eccomerce: {
                            ...analyticsEvents[0].ecommerce,
                        },
                    }),
                ];
                if (analyticsEvents.length === 2) {
                    actions.push(AnalyticsActions.SendCustomAnalytics({
                        eventData: {
                            ...analyticsEvents[1].eventData,
                            eventFrom: EventFrom.checkout,
                        },
                        customData: {
                            ...analyticsEvents[1].customData,
                        },
                        eccomerce: {
                            ...analyticsEvents[1].ecommerce,
                        },
                    }));
                }
                return actions;
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public setNextStatus$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationButtonActions.SetNextStatus),
            map(() => AppEventsAnalyticsActions.CheckoutNextStepAnalytics())
        );
    });

    public checkoutBack$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.CheckoutBackAnalytics),
            map(({ destination }) => imCheckoutSendCheckoutBackEvent(destination)),
            map((eventData) =>
                AnalyticsActions.AnalyticsEvent({
                    eventData: {
                        ...eventData,
                        eventFrom: EventFrom.checkout,
                    },
                })),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public CheckoutSelectPlace$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.CheckoutSelectPlaceAnalytics),
            map((payload) =>
                imCheckoutSendCheckoutSelectPlaceEvent({
                    placeId: payload.placeId,
                    shippingMethodKind: payload.shippingMethodKind,
                    source: payload.source,
                })),
            map(({ eventData, customData }) =>
                AnalyticsActions.SendCustomAnalytics({
                    eventData: {
                        ...eventData,
                        eventFrom: EventFrom.checkout,
                    },
                    customData,
                })),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public CheckoutConfirmPlace$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.CheckoutConfirmPlaceAnalytics),
            map((payload) =>
                imCheckoutSendCheckoutConfirmPlaceEvent({
                    placeId: payload.placeId,
                    shippingMethodKind: payload.shippingMethodKind,
                })),
            map(({ eventData, customData }) =>
                AnalyticsActions.SendCustomAnalytics({
                    eventData: {
                        ...eventData,
                        eventFrom: EventFrom.checkout,
                    },
                    customData,
                })),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public openPurchaseConditionsEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenDocument),
            filter((documentType) => documentType.name !== 'generalConditions'),
            map(() => imCheckoutOpenPurchaseConditions()),
            map((eventData) => {
                return AnalyticsActions.AnalyticsEvent({
                    eventData: {
                        ...eventData,
                        eventFrom: EventFrom.checkout,
                    },
                });
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public openPrivacyPolicyEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenDocument),
            filter((documentType) => documentType.name !== 'privacyPolicy'),
            map(() => imCheckoutOpenPrivacyPolicy()),
            map((eventData) => {
                return AnalyticsActions.AnalyticsEvent({
                    eventData: {
                        ...eventData,
                        eventFrom: EventFrom.checkout,
                    },
                });
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public billingDataOkEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.CheckoutBillingDataOkAnalytics),
            map(() => imCheckoutCheckoutBillingDataOk()),
            map((eventData) => {
                return AnalyticsActions.AnalyticsEvent({
                    eventData: {
                        ...eventData,
                        eventFrom: EventFrom.checkout,
                    },
                });
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public cardAddedOk$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.EventPaymentOkAnalytics),
            concatLatestFrom(() => [this.store.select(getPaymentDetailsValid), this.store.select(getPaymentDetails)]),
            filter(([_, isPaymentDetailsValid, paymentDetails]) => isPaymentDetailsValid && paymentDetails.paymentMethodKind === 'credit_card'),
            map(([_, __, paymentDetails]) => {
                return AnalyticsEvent({
                    eventData: {
                        cf: 'checkout',
                        category: 'pago',
                        action: 'tarjeta_anadida_ok',
                        label: paymentDetails.paymentMethodType,
                        eventFrom: EventFrom.checkout,
                    },
                });
            })
        );
    });

    public setPickupPointsViewModeAnalitics = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.SetPickupDisplayModeAnalytics),
            map((action) => action.payload),
            concatLatestFrom(() => this.store.select(getSelectedShippingMethod)),
            filter(([_, shippingMethod]) => !!shippingMethod),
            map(([viewMode, shippingMethod]) => {
                const pickupType = {
                    droppoint: 'localizador_droppoint',
                    pickup: 'localizador_tiendas',
                };
                const displayMode = {
                    list: 'ver_lista',
                    map: 'ver_mapa',
                };
                const eventData = {
                    cf: pickupType[shippingMethod.kind],
                    action: displayMode[viewMode],
                };

                return ShippingEvent({ eventData });
            })
        );
    });

    public setSelectedPickupPointAnalitics$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetSelectedPickupPoint),
            map((action) => action.payload),
            concatLatestFrom(() => [
                this.store.select(getSelectedShippingMethod),
                this.store.select(getPickupPointsViewMode),
            ]),
            map(([pickupDetails, shippingMethod, viewMode]: [DroppointListsDetail | PhysicalStoreDetail, ShippingMethod, 'list' | 'map']) => {
                return this.imShippingService.createPickupSelectEvent(
                    shippingMethod,
                    viewMode,
                    pickupDetails
                );
            }),
            map(({ eventData, customData }) => {
                return ShippingEvent({ eventData, customData });
            })
        );
    });
}
